import React, { createContext, useContext, useState, useEffect } from "react";

// ── Available currencies ─────────────────────────────────────────────────────
export const CURRENCIES = [
  { code: "USD", symbol: "$",   label: "USD — US Dollar"        },
  { code: "EUR", symbol: "€",   label: "EUR — Euro"             },
  { code: "GBP", symbol: "£",   label: "GBP — British Pound"    },
  { code: "RUB", symbol: "₽",   label: "RUB — Ruble"            },
  { code: "TRY", symbol: "₺",   label: "TRY — Turkish Lira"     },
  { code: "THB", symbol: "฿",   label: "THB — Thai Baht"        },
  { code: "AED", symbol: "AED", label: "AED — UAE Dirham"       },
  { code: "INR", symbol: "₹",   label: "INR — Indian Rupee"     },
  { code: "AUD", symbol: "A$",  label: "AUD — Australian Dollar"},
];

// ── Hardcoded "beautiful" prices per plan per currency ───────────────────────
export const PLAN_PRICES = {
  pro_monthly:     { USD: "9.99",   EUR: "9.99",   GBP: "7.99",  RUB: "899",   TRY: "319",  THB: "349",  AED: "36.99", INR: "829",   BRL: "49.90", AUD: "14.99"  },
  pro_yearly:      { USD: "99.99",  EUR: "99.99",  GBP: "79.99", RUB: "8990",  TRY: "3190", THB: "3490", AED: "369",   INR: "8290",  BRL: "499",   AUD: "149.99" },
  premium_monthly: { USD: "19.99",  EUR: "18.99",  GBP: "15.99", RUB: "1799",  TRY: "629",  THB: "699",  AED: "73.99", INR: "1659",  BRL: "99.90", AUD: "29.99"  },
  premium_yearly:  { USD: "199.99", EUR: "189.99", GBP: "159.99",RUB: "17990", TRY: "6290", THB: "6990", AED: "739",   INR: "16590", BRL: "999",   AUD: "299.99" },
};

// ── Country → currency mapping ───────────────────────────────────────────────
const COUNTRY_TO_CURRENCY = {
  // USD
  US: "USD", CA: "USD", MX: "USD", JM: "USD", PA: "USD",
  // GBP
  GB: "GBP",
  // AUD
  AU: "AUD", NZ: "AUD",
  // RUB
  RU: "RUB", BY: "RUB", KZ: "RUB",
  // TRY
  TR: "TRY",
  // THB
  TH: "THB",
  // AED
  AE: "AED", SA: "AED", QA: "AED", KW: "AED", BH: "AED", OM: "AED",
  // INR
  IN: "INR",
  // Trybit does not support BRL as invoice fiat currency yet.
  BR: "USD",
  // EUR — EU member states + close neighbours
  DE: "EUR", FR: "EUR", IT: "EUR", ES: "EUR", NL: "EUR", BE: "EUR",
  AT: "EUR", PT: "EUR", GR: "EUR", FI: "EUR", IE: "EUR", LU: "EUR",
  PL: "EUR", CZ: "EUR", HU: "EUR", RO: "EUR", BG: "EUR", HR: "EUR",
  SK: "EUR", SI: "EUR", EE: "EUR", LV: "EUR", LT: "EUR", MT: "EUR",
  CY: "EUR", SE: "EUR", NO: "EUR", DK: "EUR", CH: "EUR", UA: "EUR",
  IL: "EUR", AM: "EUR", GE: "EUR", AZ: "EUR", RS: "EUR", ME: "EUR",
};

const LS_KEY = "sm_currency";
const SUPPORTED_CURRENCY_CODES = new Set(CURRENCIES.map((c) => c.code));

const CurrencyContext = createContext(null);

export function CurrencyProvider({ children }) {
  // If user manually picked a currency it's in localStorage; otherwise detect
  const [currency, setCurrencyState] = useState(
    () => {
      const saved = localStorage.getItem(LS_KEY);
      return SUPPORTED_CURRENCY_CODES.has(saved) ? saved : "USD";
    }
  );
  const [loading, setLoading] = useState(!localStorage.getItem(LS_KEY));

  useEffect(() => {
    if (localStorage.getItem(LS_KEY)) return; // already overridden by user
    fetch("https://ipapi.co/json/")
      .then((r) => r.json())
      .then((data) => {
        const code = data.country_code;
        const cur = COUNTRY_TO_CURRENCY[code] || "USD";
        setCurrencyState(cur);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  /** Persist manual override */
  const setCurrency = (code) => {
    const normalized = SUPPORTED_CURRENCY_CODES.has(code) ? code : "USD";
    localStorage.setItem(LS_KEY, normalized);
    setCurrencyState(normalized);
  };

  /** Reset to auto-detect (removes override) */
  const resetCurrency = () => {
    localStorage.removeItem(LS_KEY);
    setLoading(true);
    fetch("https://ipapi.co/json/")
      .then((r) => r.json())
      .then((data) => {
        const code = data.country_code;
        setCurrencyState(COUNTRY_TO_CURRENCY[code] || "USD");
      })
      .catch(() => setCurrencyState("USD"))
      .finally(() => setLoading(false));
  };

  /** Returns { amount: "9.99", symbol: "$", display: "$9.99" } for a given planId */
  const getPrice = (planId) => {
    const row = PLAN_PRICES[planId];
    if (!row) return { amount: "0", symbol: "$", display: "Free" };
    const amount = row[currency] ?? row.USD;
    const sym = CURRENCIES.find((c) => c.code === currency)?.symbol || "$";
    return { amount, symbol: sym, display: `${sym}${amount}` };
  };

  const currencyObj = CURRENCIES.find((c) => c.code === currency) || CURRENCIES[0];

  return (
    <CurrencyContext.Provider
      value={{ currency, setCurrency, resetCurrency, getPrice, currencies: CURRENCIES, currencyObj, loading }}
    >
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used inside CurrencyProvider");
  return ctx;
}
